import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ClipboardTooltip } from "@/components/utils/clipboard-tooltip";

interface DeleteConfirmationModalProps {
  isOpen: boolean;
  isCritical: boolean;
  itemName?: string;
  onClose: () => void;
  onConfirm: () => void;
  confirmationValue?: string;
  onConfirmationChange?: (value: string) => void;
  canConfirm?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  confirmVariant?: "destructive" | "default";
  isLoading?: boolean;
}

export function DeleteConfirmationModal({
  isOpen,
  isCritical,
  itemName,
  onClose,
  onConfirm,
  confirmationValue = "",
  onConfirmationChange,
  canConfirm = true,
  title = "¿Estás seguro?",
  description = "Esta acción no se puede deshacer.",
  confirmLabel = "Eliminar",
  confirmVariant = "destructive",
  isLoading = false,
}: DeleteConfirmationModalProps) {
  const handleConfirm = () => {
    if (canConfirm) {
      onConfirm();
      onClose();
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription asChild>
            <div className="text-muted-foreground text-sm">
              {description}
              {isCritical && itemName && (
                <>
                  <strong>Esta es una acción crítica.</strong> Para confirmar,
                  escribe el nombre del elemento: <br /> <br />
                  <ClipboardTooltip value={itemName} content={itemName} />
                </>
              )}
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>

        {isCritical && (
          <div className="space-y-2">
            <Label htmlFor="confirmation">
              Escribe o copia y pega "{itemName}" para confirmar
            </Label>
            <Input
              autoFocus
              id="confirmation"
              value={confirmationValue}
              onChange={(e) => onConfirmationChange?.(e.target.value)}
              placeholder={itemName}
              required
            />
          </div>
        )}

        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={!canConfirm || isLoading}
            className={
              confirmVariant === "destructive"
                ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                : ""
            }
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
